export const SellerPerspective = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-teal-50 border border-teal-200 rounded-full mb-6">
            <span className="text-teal-600 text-sm font-semibold">Seller's Strategy</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Maximizing Your Sale Price Through T12 Optimization
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Smart sellers understand that improving T12 performance before listing can add hundreds of thousands to their sale price. Here's your strategic roadmap.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <div className="aspect-w-4 aspect-h-3 rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://public.readdy.ai/ai/img_res/0c29760f3788c54e7913fc3826dadd4d.jpg"
                alt="Property owner strategizing T12 optimization for maximum sale price"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">The 6-12 Month Pre-Sale Strategy</h3>
              <p className="text-gray-700 mb-4">
                If you're planning to sell within the next year, focus on optimizing your T12 now. Small improvements compound into significant value increases.
              </p>
              
              <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-6 text-white">
                <h4 className="font-bold mb-3">ROI Example</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Investment in improvements:</span>
                    <span className="font-bold">$25,000</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Annual NOI increase:</span>
                    <span className="font-bold">$30,000</span>
                  </div>
                  <div className="flex justify-between border-t border-white/30 pt-2 mt-2">
                    <span>Value increase (6.5% cap):</span>
                    <span className="font-bold text-xl">$461,538</span>
                  </div>
                  <div className="flex justify-between border-t border-white/30 pt-2 mt-2">
                    <span>Net gain:</span>
                    <span className="font-bold text-2xl">$436,538</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Wins for T12 Improvement</h3>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-arrow-up-line text-green-600"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Raise Below-Market Rents</div>
                    <p className="text-sm text-gray-700">If your rents are 10% below market, raising them adds $34,560/year to a 24-unit property = $531,692 in value at 6.5% cap rate.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-door-closed-line text-green-600"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Fill Vacant Units</div>
                    <p className="text-sm text-gray-700">Reducing vacancy from 10% to 5% adds $17,280/year = $265,846 in value. Focus on this 3-6 months before listing.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-money-dollar-circle-line text-green-600"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Add Income Sources</div>
                    <p className="text-sm text-gray-700">Pet fees ($25/pet), parking ($50/space), storage ($50/unit) can add $10,000-20,000/year = $153,846-307,692 in value.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-arrow-down-line text-blue-600"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Reduce Utility Costs</div>
                    <p className="text-sm text-gray-700">Water-saving fixtures, LED lighting, programmable thermostats can cut utilities 15-25% = $2,000-4,000/year = $30,769-61,538 in value.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-blue-600"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Shop Insurance Rates</div>
                    <p className="text-sm text-gray-700">Getting 3-5 quotes can save $3,000-6,000/year = $46,154-92,308 in value. Takes 2 hours of work.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200 mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">T12 Optimization Timeline</h3>
          
          <div className="space-y-6">
            <div className="flex gap-6">
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-teal-600 font-bold text-xl">12</span>
                </div>
                <div className="w-1 h-full bg-teal-200 mt-2"></div>
              </div>
              <div className="flex-1 pb-8">
                <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-6 border border-gray-200">
                  <div className="font-bold text-gray-900 mb-2">12 Months Before Sale</div>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                      <span>Analyze current T12 and identify improvement opportunities</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                      <span>Research market rents and adjust pricing strategy</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                      <span>Implement expense reduction initiatives</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                      <span>Address deferred maintenance issues</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="flex gap-6">
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-teal-600 font-bold text-xl">9</span>
                </div>
                <div className="w-1 h-full bg-teal-200 mt-2"></div>
              </div>
              <div className="flex-1 pb-8">
                <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-6 border border-gray-200">
                  <div className="font-bold text-gray-900 mb-2">9 Months Before Sale</div>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                      <span>Begin raising rents to market rates (at lease renewals)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                      <span>Implement new income sources (pet fees, parking, etc.)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                      <span>Focus on filling vacant units</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                      <span>Track improvements in monthly financials</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="flex gap-6">
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-teal-600 font-bold text-xl">6</span>
                </div>
                <div className="w-1 h-full bg-teal-200 mt-2"></div>
              </div>
              <div className="flex-1 pb-8">
                <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-6 border border-gray-200">
                  <div className="font-bold text-gray-900 mb-2">6 Months Before Sale</div>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                      <span>Review T12 progress and adjust strategies</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                      <span>Organize all financial documentation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                      <span>Have accountant review T12 for accuracy</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                      <span>Address any remaining red flags</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="flex gap-6">
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-teal-600 font-bold text-xl">3</span>
                </div>
                <div className="w-1 h-full bg-teal-200 mt-2"></div>
              </div>
              <div className="flex-1 pb-8">
                <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-6 border border-gray-200">
                  <div className="font-bold text-gray-900 mb-2">3 Months Before Sale</div>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                      <span>Finalize T12 with all improvements reflected</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                      <span>Prepare complete due diligence package</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                      <span>Get property professionally valued</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                      <span>Interview potential brokers or buyers</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="flex gap-6">
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-2xl text-green-600"></i>
                </div>
              </div>
              <div className="flex-1">
                <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-6 border-2 border-green-200">
                  <div className="font-bold text-gray-900 mb-2">List Property for Sale</div>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                      <span>Present strong T12 showing optimized performance</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                      <span>Provide complete documentation package immediately</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                      <span>Command premium pricing based on solid financials</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                      <span>Close quickly with confident buyers</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-6 border border-green-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-time-line text-2xl text-green-600"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">When to Wait Before Selling</h3>
                <p className="text-gray-700 mb-3">
                  Consider delaying your sale if:
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-green-600 mt-0.5"></i>
                    <span>Your T12 shows declining income trends that can be reversed</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-green-600 mt-0.5"></i>
                    <span>Vacancy is above 10% but you can fill units within 6 months</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-green-600 mt-0.5"></i>
                    <span>Rents are 15%+ below market and leases renew soon</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-green-600 mt-0.5"></i>
                    <span>You can add $30,000+ to NOI with minimal investment</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-6 border border-yellow-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-speed-line text-2xl text-yellow-600"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">When to Sell Now</h3>
                <p className="text-gray-700 mb-3">
                  Sell immediately if:
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-yellow-600 mt-0.5"></i>
                    <span>Your T12 is strong and shows consistent performance</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-yellow-600 mt-0.5"></i>
                    <span>Market cap rates are favorable (low rates = high values)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-yellow-600 mt-0.5"></i>
                    <span>Major capital expenditures are needed soon</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-yellow-600 mt-0.5"></i>
                    <span>You need liquidity and can't wait 6-12 months</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};